import React, { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react';

const TypewriterContext = createContext();

export const useTypewriter = () => {
  const context = useContext(TypewriterContext);
  if (!context) {
    throw new Error('useTypewriter must be used within a TypewriterProvider');
  }
  return context;
};

export const TypewriterProvider = ({ children }) => {
  const [enabled, setEnabled] = useState(true);
  const [isActive, setIsActive] = useState(false);
  const [queue, setQueue] = useState([]);
  const timeoutRef = useRef(null);
  const registeredIds = useRef(new Set());

  // Fonction pour enregistrer un texte à animer
  const registerText = useCallback((config) => {
    const id = config.text.substring(0, 20) + '-' + Date.now();
    if (registeredIds.current.has(id)) return () => {};

    registeredIds.current.add(id);
    const newItem = { id, ...config };
    
    setQueue(prevQueue => [...prevQueue, newItem]);

    return () => {
      setQueue(prevQueue => prevQueue.filter(item => item.id !== id));
      registeredIds.current.delete(id);
    };
  }, []);

  // Effet qui traite la file d'attente
  useEffect(() => {
    if (isActive || queue.length === 0 || !enabled) {
      return;
    }

    const currentItem = queue[0];
    setIsActive(true);

    let charIndex = 0;
    const typeChar = () => {
      if (charIndex < currentItem.text.length) {
        const displayedText = currentItem.text.slice(0, charIndex + 1);
        currentItem.onUpdate(displayedText, true);
        charIndex++;
        timeoutRef.current = setTimeout(typeChar, currentItem.speed);
      } else {
        // Animation de l'item terminée
        currentItem.onUpdate(currentItem.text, false);
        currentItem.onComplete();
        
        // Passer au suivant après le délai
        timeoutRef.current = setTimeout(() => {
          setQueue(prevQueue => prevQueue.slice(1)); // Retire l'élément traité
          setIsActive(false); // Permet à l'useEffect de prendre le relais
        }, currentItem.delayAfter);
      }
    };

    // Démarrer l'animation de l'item après son délai initial
    timeoutRef.current = setTimeout(typeChar, currentItem.delayBefore || 100);

    return () => {
      clearTimeout(timeoutRef.current);
    };
  }, [queue, isActive, enabled]);

  const stopAll = useCallback(() => {
    clearTimeout(timeoutRef.current);
    setIsActive(false);
  }, []);

  const reset = useCallback(() => {
    setQueue([]);
    registeredIds.current.clear();
    stopAll();
  }, [stopAll]);

  const value = {
    registerText,
    stopAll,
    reset,
    enabled,
    setEnabled,
    isActive,
    queueLength: queue.length,
    completedCount: 0 // Simplifié pour l'instant
  };

  return (
    <TypewriterContext.Provider value={value}>
      {children}
    </TypewriterContext.Provider>
  );
};
